import { c as _c } from "react/compiler-runtime";
import { useContext } from 'react';
import { captureException } from '@sentry/nextjs';
import { LoginTemplate, PublicUser } from '@sbt-web/auth';
import { OptimizelySubitoContext } from '@sbt-web/houston-wrapper';
import { getUserSavedSearch, saveSearch } from '@sbt-web/network/client';
import { HTTPStatusCode, NetError, SavedSearch } from '@sbt-web/network/types';
import { FiltersConfigState } from '@/reducers/filtersConfig';
import { SearchState } from '@/reducers/search';
import { HADES_PATH, MOBILE_API_CHANNEL, WEB_API_CHANNEL } from '@/shared/constants';
import { isMobileViewport } from '@/shared/helpers/Screen';
import { sendNotification } from '@/shared/tools/Notifications';
import useUser from '../../../client/contexts/UserContext';
import { mapToSavedSearch } from './stateTranslator';
import { DefaultTrackingEvents, SavedSearchesEvents } from './trackingEvents';
import notificationClasses from './notification.module.scss';
const NotificationSaveSearch = () => {
  const $ = _c(2);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <br className={notificationClasses["message-line-break"]} />;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <p className="body-text">Verrai avvisato sui nuovi annunci.{" "}{t0}Gestisci le tue{" "}<a className={notificationClasses.link} href={`${process.env.NEXT_PUBLIC_AREARISERVATA_BASE_URL}/ricerche`}>ricerche salvate</a></p>;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
};
export interface SaveSearchProvider {
  saveSearch(search: SearchState, filtersConfig: FiltersConfigState): Promise<void>;
  getSavedSearches(): Promise<Array<SavedSearch>>;
}
export function useSavedSearches(savedSearchesEvents: SavedSearchesEvents = new DefaultTrackingEvents()): SaveSearchProvider {
  const {
    user,
    requestLogin
  } = useUser();
  const {
    optimizely
  } = useContext(OptimizelySubitoContext);
  function requestLoginThenSave(search: SearchState, filtersConfig: FiltersConfigState): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      requestLogin(LoginTemplate.MySearches, user_0 => {
        savedSearchesEvents.onLoginInPlace(search, user_0);
        privateSaveSearch(search, user_0, filtersConfig).then(resolve, reject);
      });
    });
  }
  async function privateGetSavedSearches(user_1: PublicUser): Promise<Array<SavedSearch>> {
    try {
      const {
        body
      } = await getUserSavedSearch(HADES_PATH, user_1.id);
      return body.savedSearches;
    } catch {
      return [];
    }
  }
  async function privateSaveSearch(search_0: SearchState, user_2: PublicUser, filtersConfig_0: FiltersConfigState): Promise<void> {
    try {
      const channel = isMobileViewport() ? MOBILE_API_CHANNEL : WEB_API_CHANNEL;
      const {
        body: body_0
      } = await saveSearch(HADES_PATH, user_2.id, mapToSavedSearch(search_0, filtersConfig_0), channel);
      optimizely?.track('save_search_click', user_2.id);
      savedSearchesEvents.onSave(search_0, body_0);
      sendNotification(<NotificationSaveSearch />, 'success');
      window.dispatchEvent(new Event('subito:saveSearch:saved'));
    } catch (e) {
      if (e instanceof NetError && e.cause.status === HTTPStatusCode.Unauthorized) {
        return requestLoginThenSave(search_0, filtersConfig_0);
      }
      sendNotification('Impossibile salvare la ricerca', 'error');
      captureException(new Error(`Unable to save the search due to: ${e}`));
    }
  }
  return {
    getSavedSearches: async () => {
      if (user === null) {
        return [];
      } else {
        return await privateGetSavedSearches(user);
      }
    },
    saveSearch: async (search_1, filtersConfig_1): Promise<void> => {
      if (!search_1) {
        throw new Error('Missing required parameters');
      }
      savedSearchesEvents.onBeforeSave(search_1);
      if (user === null) {
        await requestLoginThenSave(search_1, filtersConfig_1);
      } else {
        await privateSaveSearch(search_1, user, filtersConfig_1);
      }
    }
  };
}